import { useExperienceApiQuery } from '../../hooks/useExperienceApiQuery';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  Body2,
  Body3,
  Box,
  ButtonColorVariant,
  Color,
  Heading3,
  Heading5,
  Join,
  ModalBody,
  ModalButton,
  ModalFooter,
  Space,
} from '@gousto-internal/citrus-react';
import { RecipeImage } from '../RecipeImage/RecipeImage';
import { Ingredients } from './Ingredients';
import { RecipeModalContentsProps } from './types';
import { Allergens } from './Allergens';
import { NutritionalInformation } from './NutritionalInformation';
import { LoadingSkeleton } from '../Skeleton/Skeleton';
import { IngredientsContain } from './IngredientsContain';
import { CookingSection } from './CookingSection';

export function RecipeModalContents(props: RecipeModalContentsProps) {
  const { data, isLoading, error, mutate } = useExperienceApiQuery(
    '/public/v1/recipes/{recipeId}',
    {
      params: {
        path: { recipeId: props.id },
      },
    },
  );
  const cookingInstructionsUiEnabled = useFeatureFlags('enable_recipe_cooking_instructions_ui');

  const ingredients = data?.data?.ingredients || [];
  const allergens = data?.data?.allergens || [];

  const shouldShowError = error && !isLoading;
  const shouldShowCookingSection = props.isLoggedIn && cookingInstructionsUiEnabled;

  const footerContents = error ? (
    <ModalButton width="100%" colorVariant={ButtonColorVariant.Secondary} onClick={() => mutate()}>
      Try again
    </ModalButton>
  ) : (
    props.footer
  );

  return (
    <>
      <ModalBody paddingV={[0]} paddingH={[4, null, 0]}>
        <Box height={['200px', null, '315px']}>
          <RecipeImage primaryImageUrl={props.primaryImage} />
        </Box>
        <Box paddingV={[4, null, 6]} paddingH={[0, null, 6]}>
          {isLoading ? <Skeleton /> : null}
          {shouldShowError && (
            <Heading3 color={Color.ColdGrey_800}>
              Oops! Something went wrong loading this recipe. Please try again in a moment.
            </Heading3>
          )}
          {data?.data && (
            <Join with={<Space size={4} />}>
              <Body2 id="recipe-modal-description">{props.description}</Body2>
              {ingredients.length ? <Ingredients ingredients={ingredients} /> : null}
              {data.data?.nutritionalInformation ? (
                <NutritionalInformation nutritionalInformation={data.data.nutritionalInformation} />
              ) : null}
              {ingredients.length ? (
                <IngredientsContain ingredients={ingredients} recipeAllergens={allergens} />
              ) : null}
              <Allergens allergens={allergens} />
              {shouldShowCookingSection && (
                <CookingSection
                  recipeId={props.id}
                  cookingInstructionsModalName={props.cookingInstructionsModalName}
                  onStepsFetched={props.onCookingStepsFetched}
                />
              )}
            </Join>
          )}
        </Box>
      </ModalBody>
      {footerContents && <ModalFooter>{footerContents}</ModalFooter>}
    </>
  );
}

function Skeleton() {
  return (
    <Box>
      <Heading5>
        <LoadingSkeleton width="30%" />
      </Heading5>
      <Space size={4} />
      <Join with={<Space size={2} />}>
        <Body3>
          <LoadingSkeleton width="60%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="80%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="70%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="60%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="50%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="70%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="60%" />
        </Body3>
        <Body3>
          <LoadingSkeleton width="30%" />
        </Body3>
      </Join>
    </Box>
  );
}
